const RELOAD_AT_KEY = '__myfabrik_chunk_reload_at__'
const RELOAD_URL_PARAM = '__mf_reload'
const RELOAD_TTL_MS = 30_000
const INSTALL_FLAG = '__myfabrikChunkReloadHandlersInstalled'

declare global {
  interface Window {
    __myfabrikChunkReloadHandlersInstalled?: boolean
  }
}

function reasonToText(reason: unknown): string {
  if (!reason) return ''
  if (typeof reason === 'string') return reason

  if (typeof reason === 'object') {
    const value = reason as {
      name?: unknown
      message?: unknown
      stack?: unknown
      payload?: unknown
      detail?: unknown
    }
    return [
      value.name,
      value.message,
      value.stack,
      reasonToText(value.payload),
      reasonToText(value.detail),
    ].filter(Boolean).join(' ')
  }

  return String(reason)
}

function readLastReloadAt(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_AT_KEY) || 0)
  } catch {
    return 0
  }
}

function writeLastReloadAt(value: number) {
  try {
    window.sessionStorage.setItem(RELOAD_AT_KEY, String(value))
  } catch {
    // Certains contextes privacy peuvent refuser sessionStorage ; le reload reste
    // préférable à une page blanche, même sans garde persistante parfaite.
  }
}

function clearStaleReloadFlag(now = Date.now()) {
  try {
    const lastReloadAt = readLastReloadAt()
    if (!lastReloadAt || now - lastReloadAt > RELOAD_TTL_MS) {
      window.sessionStorage.removeItem(RELOAD_AT_KEY)
    }
  } catch {
    // Rien à faire : sessionStorage est optionnel pour ce garde-fou.
  }
}

function hasRecentReload(now: number): boolean {
  const lastReloadAt = readLastReloadAt()
  if (!lastReloadAt) return false
  if (now - lastReloadAt <= RELOAD_TTL_MS) return true
  clearStaleReloadFlag(now)
  return false
}

function stripReloadParam() {
  try {
    const url = new URL(window.location.href)
    if (!url.searchParams.has(RELOAD_URL_PARAM)) return
    url.searchParams.delete(RELOAD_URL_PARAM)
    const cleanUrl = `${url.pathname}${url.search}${url.hash}`
    window.history.replaceState(window.history.state, '', cleanUrl)
  } catch {
    // Nettoyage cosmétique uniquement.
  }
}

export function isChunkLoadError(reason: unknown): boolean {
  const text = reasonToText(reason)
  if (!text) return false
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading chunk \d+ failed|Unable to preload CSS|modulepreload/i.test(text)
}

/**
 * Vrai si l'erreur signalée par `vite:preloadError` n'est qu'un PRÉCHARGEMENT
 * raté (CSS), que Vite peut ignorer pour poursuivre l'import. Faux pour l'échec
 * de l'import lui-même : l'avaler résoudrait le module à `undefined`.
 */
export function isSwallowablePreloadError(payload: unknown): boolean {
  return /Unable to preload CSS/i.test(reasonToText(payload))
}

export function reloadPageWithCacheBust() {
  const url = new URL(window.location.href)
  url.searchParams.set(RELOAD_URL_PARAM, String(Date.now()))
  window.location.replace(url.toString())
}

export function reloadForChunkLoadError(reason: unknown): boolean {
  if (typeof window === 'undefined' || !isChunkLoadError(reason)) return false

  // Le garde-fou inline du SSR (`staticAssetGuard.ts`) est le propriétaire
  // unique de la reprise : il compte les essais, les espace, et finit par
  // AFFICHER un message. Sans cette délégation les deux mécanismes comptaient
  // séparément et celui-ci renonçait en silence — un rechargement dans les 30 s
  // suffisait à ce qu'un chunk manquant laisse une page morte, sans un mot pour
  // le visiteur (constaté sur `messages-<hash>.js` le 06/09/2026, catalogue de
  // traductions importé à l'évaluation de `i18n.ts`).
  const guard = (window as Window & { __mfDeployGuard?: { fail?: (detail?: string) => void } }).__mfDeployGuard
  if (guard?.fail) {
    // Le message d'erreur nomme le chunk introuvable : il part avec le signalement.
    guard.fail(reasonToText(reason).slice(0, 480))
    return true
  }

  // Repli quand la page n'a pas été rendue par le SSR (dev Vite pur, tests) :
  // comportement historique, un seul rechargement par fenêtre de 30 s.
  const now = Date.now()
  if (hasRecentReload(now)) return false

  writeLastReloadAt(now)
  reloadPageWithCacheBust()
  return true
}

export function installChunkReloadHandlers() {
  if (typeof window === 'undefined' || window[INSTALL_FLAG]) return

  window[INSTALL_FLAG] = true
  clearStaleReloadFlag()
  stripReloadParam()

  // Vite émet cet événement dans DEUX cas : un préchargement CSS raté, et
  // l'échec de l'import lui-même (`baseModule().catch(handlePreloadError)`).
  // `preventDefault` dit à Vite « ne relance pas l'erreur » : sur un import
  // raté, la promesse se résout alors avec `undefined`, `React.lazy` lit
  // `undefined.default` et la page reste BLANCHE, sans aucun message
  // (« e._result.default » sous Safari, 01/10/2026, chunk AdminImageManager
  // disparu après un déploiement). On n'avale donc que le préchargement CSS ;
  // l'import raté remonte jusqu'à `ChunkErrorBoundary` / `unhandledrejection`.
  window.addEventListener('vite:preloadError', (event: Event) => {
    if (isSwallowablePreloadError((event as Event & { payload?: unknown }).payload)) {
      event.preventDefault()
    }
  })

  window.addEventListener('error', (event) => {
    if (reloadForChunkLoadError(event.error || event.message)) {
      event.preventDefault()
    }
  })

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (reloadForChunkLoadError(event.reason)) {
      event.preventDefault()
    }
  })
}
